import { yupResolver } from "@hookform/resolvers/yup";
import SearchIcon from "@mui/icons-material/Search";
import { Grid, Tab, Tabs } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { IdentificacionTextField } from "@/erp/compras/proveedores/shared/components/IdentificacionProveedorTextField";
import {
  BucketAwtENUM,
  ROUTER_PATHS,
  TIPO_IDENTIFICACIONES_CHOICE,
  TipoIdentificacionEnumChoice,
  a11yProps,
  gridSize,
  gridSizeMdLg6,
  newEmpresaSchema,
  useLoaders,
  useUploadFile,
  useUploadImagesGeneric,
} from "@/shared";
import {
  CreateEmpresaParams,
  useCreateEmpresa,
  useFetchAllCountries,
  useFetchCitiesEnabled,
  useFetchProvinces,
  useFetchSearchIdentification,
  useFetchSectors,
  useFetchTiposRegimen,
  useFetchZones,
  useUpdateEmpresa,
} from "@/shared/actions/erp";
import {
  CustomAutocomplete,
  CustomAutocompleteArrString,
  CustomNumberTextField,
  CustomTabPanel,
  CustomTextField,
  SampleCheckbox,
  SingleIconButton,
  TabsFormBoxScene,
} from "@/shared/components/ui";
import CustomCellphoneTextField from "@/shared/components/ui/CustomCellphoneTextField";
import {
  AlertSeverityEnum,
  City,
  Country,
  Empresa,
  IDataRuc,
  Province,
  Sector,
  TYPERegimens,
  TipoDocumentoNameEnum,
  Zone,
} from "@/shared/interfaces";
import { useUiStore } from "@/store/ui";

export interface SaveEmpresaProps {
  titlePage: string;
  editEmpresa?: Empresa;
}

type SaveFormData = CreateEmpresaParams & {};

const SaveEmpresa: React.FC<SaveEmpresaProps> = ({
  titlePage,
  editEmpresa,
}) => {
  const navigate = useNavigate();
  const returnUrlEmpresasPage = ROUTER_PATHS.administration.empresaNav;

  ///* local state
  const [shrink, setShrink] = useState(false);

  // tabs
  const [flotaTab, setFlotaTab] = useState(1);
  const handleMethodChange = useCallback((_event: any, value: any) => {
    setFlotaTab(value);
  }, []);

  ///* custom hooks ------------
  const {
    UploadImageComponent,
    image1: logoImage,
    setImage1: setLogoImage,
  } = useUploadImagesGeneric();

  ///* global state -----------------------
  const setAlert = useUiStore((s) => s.setAlert);
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);

  ///* form -----------------------
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newEmpresaSchema) as any,
  });
  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  const watchedCountry = form.watch("pais");
  const watchedProvince = form.watch("provincia");
  const watchedCity = form.watch("ciudad");
  const watchedZone = form.watch("zona");
  const watchedTipoIdentificacion = form.watch("tipo_identificacion");
  const watchedIsAgentRetencion = form.watch("es_agente_retencion");

  ///* fetch data -----------------------
  const {
    data: paisesPagingRes,
    isLoading: isLoadingCountries,
    isRefetching: isRefetchingCountries,
  } = useFetchAllCountries({
    page_size: 100,
  });
  const {
    data: provinciasPagingRes,
    isLoading: isLoadingProvicias,
    isRefetching: isRefetchingProvincias,
  } = useFetchProvinces(
    {
      page_size: 1000,
      pais: watchedCountry,
    },
    !!watchedCountry
  );
  const {
    data: citiesPagingRes,
    isLoading: isLoadingCities,
    isRefetching: isRefetchingCities,
  } = useFetchCitiesEnabled({
    enabled: !!watchedProvince && !!watchedCountry,
    params: {
      page_size: 600,
      provincia: watchedProvince,
    },
  });
  const {
    data: zonasPagingRes,
    isLoading: isLoadingZonas,
    isRefetching: isRefetchingZonas,
  } = useFetchZones(
    {
      page_size: 1000,
      ciudad: watchedCity,
    },
    !!watchedCity && !!watchedProvince && !!watchedCountry
  );
  const {
    data: sectoresPagingRes,
    isLoading: isLoadingSectores,
    isRefetching: isRefetchingSectores,
  } = useFetchSectors(
    {
      page_size: 1000,
      zona: watchedZone,
    },
    !!watchedZone && !!watchedCity && !!watchedProvince && !!watchedCountry
  );

  const {
    data: tiposRegimenPaging,
    isLoading: isLoadingTiposRegimen,
    isRefetching: isRefetchingTiposRegimen,
  } = useFetchTiposRegimen({
    page_size: 200,
  });

  ///* mutations ---------------------
  const consultaIdentificacion = useFetchSearchIdentification({
    customSuccessMessage: "Datos de identificación encontrados.",
  });
  const createEmpresaMutation = useCreateEmpresa({
    navigate,
    returnUrl: returnUrlEmpresasPage,
  });
  const updateEmpresaMutation = useUpdateEmpresa({
    navigate,
    returnUrl: returnUrlEmpresasPage,
  });

  const { handleUploadFile, handleUpdateFile } = useUploadFile();
  ///* handlers -----------------------
  const onSave = async () => {
    if (!isValid) return;
    if (!logoImage && !editEmpresa?.url_logo) {
      return setAlert(true, "Debe cargar el logo de la empresa", "error");
    }
    let imageUrl;

    ///* upd
    if (editEmpresa?.id_empresa) {
      if (logoImage)
        await handleUpdateFile(
          logoImage,
          BucketAwtENUM.IMAGE_EMPRESA_LOGO,
          form.getValues().url_logo
        );

      updateEmpresaMutation.mutate({
        id: editEmpresa.id_empresa,
        data: {
          ...(form.getValues() as any),
        },
      });

      setIsGlobalLoading(false);
      return;
    }

    if (logoImage) {
      setIsGlobalLoading(true);
      imageUrl = await handleUploadFile(
        logoImage,
        BucketAwtENUM.IMAGE_EMPRESA_LOGO
      );
    }

    ///* create
    createEmpresaMutation.mutate({
      ...(form.getValues() as any),
      url_logo: imageUrl || form.getValues().url_logo,
    });
    setIsGlobalLoading(false);
  };

  const handleConsultaIdentificacion = async (value: string) => {
    setIsGlobalLoading(true);
    if (watchedTipoIdentificacion === TipoDocumentoNameEnum.CEDULA) {
      const res = await consultaIdentificacion.mutateAsync({
        identificacion: value,
      });
      setIsGlobalLoading(false);
      const { data } = res;
      if (!data) return;

      form.setValue("razon_social", data?.razon_social);

      return;
    }

    if (watchedTipoIdentificacion === TipoDocumentoNameEnum.RUC) {
      const res = await consultaIdentificacion.mutateAsync({
        identificacion: value,
      });
      setIsGlobalLoading(false);

      const data: IDataRuc = res?.data as unknown as IDataRuc;

      form.setValue("razon_social", data?.razonSocial);
      form.setValue("tipo_regimen", data?.regimen || "");
      form.setValue(
        "es_agente_retencion",
        data?.agenteRetencion === "SI" ? true : false
      );

      return;
    }

    setIsGlobalLoading(false);
  };

  ///* effects -----------------------
  useEffect(() => {
    if (editEmpresa?.id_empresa) {
      setShrink(true);

      reset({
        ...editEmpresa,
        url_logo: editEmpresa.url_logo,
      });
    }
  }, [editEmpresa?.id_empresa]);
  /// // alerts ------
  // province
  useEffect(() => {
    if (isLoadingProvicias || isRefetchingProvincias) return;

    if (watchedCountry && !provinciasPagingRes?.data?.data?.length) {
      setAlert(
        true,
        "No se encontraron provincias disponibles",
        AlertSeverityEnum.warning
      );
    }
  }, [
    watchedCountry,
    provinciasPagingRes,
    isLoadingProvicias,
    isRefetchingProvincias,
  ]);
  // city
  useEffect(() => {
    if (isLoadingCities || isRefetchingCities) return;

    if (watchedProvince && !citiesPagingRes?.data?.data?.length) {
      setAlert(
        true,
        "No se encontraron ciudades disponibles",
        AlertSeverityEnum.warning
      );
    }
  }, [watchedProvince, citiesPagingRes, isLoadingCities, isRefetchingCities]);
  // zone
  useEffect(() => {
    if (isLoadingZonas || isRefetchingZonas) return;

    if (watchedCity && !zonasPagingRes?.data?.data?.length) {
      setAlert(
        true,
        "No se encontraron zonas disponibles",
        AlertSeverityEnum.warning
      );
    }
  }, [watchedCity, zonasPagingRes, isLoadingZonas, isRefetchingZonas]);
  // sector
  useEffect(() => {
    if (isLoadingSectores || isRefetchingSectores) return;

    if (watchedZone && !sectoresPagingRes?.data?.data?.length) {
      setAlert(
        true,
        "No se encontraron sectores disponibles",
        AlertSeverityEnum.warning
      );
    }
  }, [watchedZone, sectoresPagingRes, isLoadingSectores, isRefetchingSectores]);

  useLoaders(
    isLoadingCountries ||
      isLoadingCities ||
      isLoadingProvicias ||
      isLoadingZonas ||
      isRefetchingCountries ||
      isRefetchingProvincias ||
      isRefetchingCities ||
      isRefetchingZonas ||
      isLoadingTiposRegimen ||
      isRefetchingTiposRegimen ||
      isLoadingSectores ||
      isRefetchingSectores
  );
  if (
    editEmpresa &&
    (isLoadingCountries ||
      isLoadingCities ||
      isLoadingProvicias ||
      isLoadingZonas ||
      isRefetchingCountries ||
      isRefetchingProvincias ||
      isRefetchingCities ||
      isRefetchingZonas ||
      isLoadingTiposRegimen ||
      isRefetchingTiposRegimen ||
      isLoadingSectores ||
      isRefetchingSectores)
  )
    return null;

  return (
    <TabsFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlEmpresasPage);
      }}
      onSave={handleSubmit(onSave, () => {
        setAlert(true, "Faltan campos requeridos", "error");
      })}
      tabs={
        <Tabs
          onChange={handleMethodChange}
          sx={{ mb: 6, pl: "0px" }}
          value={flotaTab}
        >
          <Tab label="Empresa" value={1} {...a11yProps(1)} />

          <Tab label="Representante Legal" value={2} {...a11yProps(2)} />
        </Tabs>
      }
    >
      {/* =================== Empresa =================== */}
      <CustomTabPanel index={flotaTab} value={1}>
        <CustomAutocompleteArrString
          label="Tipo de identificación"
          name="tipo_identificacion"
          control={form.control}
          defaultValue={form.getValues("tipo_identificacion")}
          options={
            TIPO_IDENTIFICACIONES_CHOICE.filter(
              (item) => item === TipoIdentificacionEnumChoice.RUC
            ) || []
          }
          isLoadingData={false}
          error={errors.tipo_identificacion}
          helperText={errors.tipo_identificacion?.message}
          size={gridSizeMdLg6}
          disableClearable
          onChangeValue={() => {
            form.setValue("identificacion", "");
            form.setValue("razon_social", "");
          }}
        />
        <Grid item xs={12} md={6} container alignItems="center" spacing={1}>
          <Grid item xs={12} md={10}>
            <IdentificacionTextField
              control={form.control}
              name="identificacion"
              label="Identificación"
              defaultValue={form.getValues().identificacion || ""}
              error={errors.identificacion}
              helperText={errors.identificacion?.message}
              //
              disabled={!watchedTipoIdentificacion}
              selectedDocumentType={watchedTipoIdentificacion}
              onFetchCedulaRucInfo={handleConsultaIdentificacion}
              size={gridSize}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SingleIconButton
              startIcon={<SearchIcon />}
              label="Buscar"
              color={"default" as any}
              onClick={() => {
                if (
                  !form.getValues().identificacion ||
                  !watchedTipoIdentificacion ||
                  (watchedTipoIdentificacion === TipoDocumentoNameEnum.CEDULA &&
                    form.getValues().identificacion.length !== 10) ||
                  (watchedTipoIdentificacion === TipoDocumentoNameEnum.RUC &&
                    form.getValues().identificacion.length !== 13)
                )
                  return setAlert(
                    true,
                    "Ingrese una identificación válida",
                    AlertSeverityEnum.warning
                  );

                handleConsultaIdentificacion(form.getValues().identificacion);
              }}
            />
          </Grid>
        </Grid>

        <CustomTextField
          name="razon_social"
          label="Razón Social"
          control={form.control}
          defaultValue={form.getValues().razon_social || ""}
          error={errors.razon_social}
          helperText={errors.razon_social?.message}
          shrink={shrink}
        />

        <CustomTextField
          name="nombre_comercial"
          label="Nombre Comercial"
          control={form.control}
          defaultValue={form.getValues().nombre_comercial || ""}
          error={errors.nombre_comercial}
          helperText={errors.nombre_comercial?.message}
          shrink={shrink}
        />

        <CustomTextField
          label="Correo Electrónico"
          name="email"
          control={form.control}
          error={errors.email}
          helperText={errors.email?.message}
          shrink={shrink}
          type="email"
          size={gridSizeMdLg6}
        />
        <CustomTextField
          name="direccion"
          label="Dirección"
          control={form.control}
          defaultValue={form.getValues().direccion || ""}
          error={errors.direccion}
          helperText={errors.direccion?.message}
          shrink={shrink}
          size={gridSizeMdLg6}
        />
        <CustomTextField
          name="contador"
          label="Contador"
          control={form.control}
          defaultValue={form.getValues().contador || ""}
          error={errors.contador}
          helperText={errors.contador?.message}
          shrink={shrink}
        />

        {/* ============ Location ============ */}
        <CustomAutocomplete<Country>
          label="País"
          name="pais"
          // options
          options={paisesPagingRes?.data?.data || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().pais || ""}
          optionLabelForEdit={form.getValues().pais || ""}
          isLoadingData={isLoadingCountries}
          // validation
          control={form.control}
          error={errors.pais}
          helperText={errors.pais?.message}
          size={gridSizeMdLg6}
          onChangeValue={() => {
            form.setValue("provincia", "");
            form.setValue("ciudad", "");
            form.setValue("zona", "");
            form.setValue("sector", "");
          }}
        />
        <CustomAutocomplete<Province>
          label="Provincia"
          name="provincia"
          // options
          options={provinciasPagingRes?.data?.data || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().provincia || ""}
          optionLabelForEdit={form.getValues().provincia || ""}
          isLoadingData={isLoadingProvicias}
          // validation
          control={form.control}
          error={errors.provincia}
          helperText={errors.provincia?.message}
          size={gridSizeMdLg6}
          onChangeValue={() => {
            form.setValue("ciudad", "");
            form.setValue("zona", "");
            form.setValue("sector", "");
          }}
        />
        <CustomAutocomplete<City>
          label="Ciudad"
          name="ciudad"
          // options
          options={citiesPagingRes?.data?.data || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().ciudad || ""}
          optionLabelForEdit={form.getValues().ciudad}
          isLoadingData={isLoadingCities}
          // validation
          control={form.control}
          error={errors.ciudad}
          helperText={errors.ciudad?.message}
          size={gridSizeMdLg6}
          onChangeValue={() => {
            form.setValue("zona", "");
            form.setValue("sector", "");
          }}
        />
        <CustomAutocomplete<Zone>
          label="Zona"
          name="zona"
          // options
          options={zonasPagingRes?.data?.data || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().zona || ""}
          optionLabelForEdit={form.getValues().zona}
          isLoadingData={isLoadingZonas}
          // validation
          control={form.control}
          error={errors.zona}
          helperText={errors.zona?.message}
          size={gridSizeMdLg6}
          onChangeValue={() => {
            form.setValue("sector", "");
          }}
        />
        <CustomAutocomplete<Sector>
          label="Sector"
          name="sector"
          // options
          options={sectoresPagingRes?.data?.data || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().sector || ""}
          optionLabelForEdit={form.getValues().sector}
          isLoadingData={isLoadingSectores}
          // validation
          control={form.control}
          error={errors.sector}
          helperText={errors.sector?.message}
          size={gridSizeMdLg6}
        />

        {/* ats - boolean - checbox */}
        <SampleCheckbox
          label="Crea ATS"
          name="crea_ats"
          control={form.control}
          defaultValue={!!form.getValues().crea_ats}
          size={gridSizeMdLg6}
        />

        {/* ============== fk ============== */}
        <CustomAutocomplete<TYPERegimens>
          label="Tipo de Régimen"
          name="tipo_regimen"
          // options
          options={(tiposRegimenPaging?.data.data as any) || []}
          valueKey="nombre" // name is the fk
          actualValueKey="codigo"
          defaultValue={form.getValues().tipo_regimen || ""}
          optionLabelForEdit={form.getValues().tipo_regimen || ""}
          isLoadingData={isLoadingTiposRegimen}
          // validation
          control={form.control}
          error={errors.tipo_regimen}
          helperText={errors.tipo_regimen?.message}
          disabled={false}
        />

        <SampleCheckbox
          label="Es Agente Retención"
          name="es_agente_retencion"
          control={form.control}
          defaultValue={!!form.getValues().es_agente_retencion}
          disabled
          size={!watchedIsAgentRetencion ? gridSize : gridSizeMdLg6}
        />

        {!!watchedIsAgentRetencion && (
          <CustomNumberTextField
            label="Número de Agentes de Retención"
            name="numero_agente_retencion"
            control={form.control}
            error={errors.numero_agente_retencion}
            helperText={errors.numero_agente_retencion?.message}
            shrink={shrink}
            size={gridSizeMdLg6}
            min={0}
          />
        )}

        {/* ---- logo ---- */}
        <Grid item {...gridSizeMdLg6}>
          <UploadImageComponent
            buttonText={"Logo"}
            selectedImage={logoImage}
            setSelectedImage={setLogoImage}
            imageUrl={editEmpresa?.url_logo || ""}
            onClickTrash={() => {
              reset({
                ...form.getValues(),
                url_logo: "",
              });

              setLogoImage(null);
            }}
            idInput={"empresa-logo"}
          />
        </Grid>
        <CustomTextField
          label="Código de la Empresa"
          name="codigo"
          control={form.control}
          defaultValue={form.getValues().codigo || ""}
          error={errors.codigo}
          helperText={errors.codigo?.message ?? ""}
          shrink={shrink}
          size={gridSizeMdLg6}
          disabled={!!editEmpresa}
        />
      </CustomTabPanel>

      {/* =================== Representante Legal =================== */}
      <CustomTabPanel index={flotaTab} value={2}>
        <IdentificacionTextField
          control={form.control}
          label="Identificación del Representante Legal"
          name="identificacion_representante"
          defaultValue={form.getValues().identificacion_representante || ""}
          error={errors.identificacion_representante}
          helperText={errors.identificacion_representante?.message}
          shrink={shrink}
          //
          selectedDocumentType={TipoIdentificacionEnumChoice.CEDULA}
        />
        <CustomTextField
          name="razon_social_representante"
          label="Nombre Representante Legal"
          control={form.control}
          defaultValue={form.getValues().razon_social_representante || ""}
          error={errors.razon_social_representante}
          helperText={errors.razon_social_representante?.message}
          shrink={shrink}
        />
        <CustomTextField
          label="Correo Electrónico Representante Legal"
          name="email_representante"
          control={form.control}
          error={errors.email_representante}
          helperText={errors.email_representante?.message}
          shrink={shrink}
          type="email"
          size={gridSizeMdLg6}
        />
        <CustomCellphoneTextField
          name="telefono_representante"
          label="Teléfono Representante Legal"
          control={form.control}
          defaultValue={form.getValues().telefono_representante || ""}
          error={errors.telefono_representante}
          helperText={errors.telefono_representante?.message}
          shrink={shrink}
          size={gridSizeMdLg6}
        />

        <CustomCellphoneTextField
          name="telefono_1"
          label="Teléfono 1"
          control={form.control}
          defaultValue={form.getValues().telefono_1 || ""}
          error={errors.telefono_1}
          helperText={errors.telefono_1?.message}
          shrink={shrink}
        />
        <CustomCellphoneTextField
          name="telefono_2"
          label="Teléfono 2"
          control={form.control}
          defaultValue={form.getValues().telefono_2 || ""}
          error={errors.telefono_2}
          helperText={errors.telefono_2?.message}
          shrink={shrink}
          required={false}
          size={gridSizeMdLg6}
        />
        <CustomCellphoneTextField
          name="telefono_3"
          label="Teléfono 3"
          control={form.control}
          defaultValue={form.getValues().telefono_3 || ""}
          error={errors.telefono_3}
          helperText={errors.telefono_3?.message}
          shrink={shrink}
          required={false}
          size={gridSizeMdLg6}
        />
      </CustomTabPanel>
    </TabsFormBoxScene>
  );
};

export default SaveEmpresa;
